import React, { useState, useEffect, useRef } from 'react';

interface DrawCountdownAnimationProps {
  onComplete: () => void;
  duration?: number; // Total duration in milliseconds (default 5000ms)
}

/**
 * DrawCountdownAnimation - Exciting countdown animation before revealing draw results
 * Uses Plexus brand colors and smooth animations to build anticipation
 */
const DrawCountdownAnimation: React.FC<DrawCountdownAnimationProps> = ({
  onComplete,
  duration = 5000,
}) => {
  const [currentNumber, setCurrentNumber] = useState(3);
  const [showFinal, setShowFinal] = useState(false);
  const [hideCounter, setHideCounter] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const interval = duration / 4; // Divide by 4: 3, 2, 1, GO!
    
    // Countdown sequence: 3 -> 2 -> 1
    const countdownTimers: NodeJS.Timeout[] = [];
    
    [2, 1].forEach((num, index) => {
      const timer = setTimeout(() => {
        if (isMountedRef.current) {
          setCurrentNumber(num);
        }
      }, interval * (index + 1));
      countdownTimers.push(timer);
    });

    // Show "GO!" message
    const goTimer = setTimeout(() => {
      if (isMountedRef.current) {
        setHideCounter(true);
        setTimeout(() => {
          if (isMountedRef.current) {
            setShowFinal(true);
          }
        }, 200);
      }
    }, interval * 3);
    countdownTimers.push(goTimer);

    // Complete animation and trigger callback
    const completeTimer = setTimeout(() => {
      if (isMountedRef.current) {
        onComplete();
      }
    }, duration);
    countdownTimers.push(completeTimer);

    // Cleanup
    return () => {
      countdownTimers.forEach(timer => clearTimeout(timer));
    };
  }, [duration, onComplete]);

  return (
    <>
      <style>{`
        @keyframes countdown-hide {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(0);
            opacity: 0;
          }
        }

        @keyframes countdown-show {
          0% {
            transform: translate(-50%, -50%) scale(0);
            opacity: 0;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.5);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
        }

        @keyframes countdown-goIn {
          0% {
            transform: translate(-50%, -50%) rotate(120deg);
            opacity: 0;
          }
          30% {
            transform: translate(-50%, -50%) rotate(-20deg);
            opacity: 1;
          }
          60% {
            transform: translate(-50%, -50%) rotate(10deg);
          }
          90%, 100% {
            transform: translate(-50%, -50%) rotate(0deg);
          }
        }

        @keyframes countdown-goOut {
          0%, 30% {
            transform: translate(-50%, -50%) rotate(0deg);
            opacity: 1;
          }
          60% {
            transform: translate(-50%, -50%) rotate(20deg);
            opacity: 0.5;
          }
          100% {
            transform: translate(-50%, -50%) rotate(-120deg);
            opacity: 0;
          }
        }

        @keyframes countdown-pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        .countdown-number-in {
          animation: countdown-goIn 0.5s ease-in-out;
        }

        .countdown-number-out {
          animation: countdown-goOut 0.5s ease-in-out;
        }
      `}</style>

      {/* Fullscreen overlay with gradient background */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, var(--plexus-color-positive-900) 0%, var(--plexus-color-positive-700) 50%, var(--plexus-color-positive-500) 100%)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Countdown counter */}
        {!hideCounter && (
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              animation: hideCounter ? 'countdown-hide 0.2s ease-out forwards' : 'none',
            }}
          >
            {/* Build-up text */}
            <div
              style={{
                fontSize: '1.5rem',
                fontWeight: 500,
                color: 'var(--plexus-color-positive-100)',
                marginBottom: 'var(--plexus-space-xl)',
                letterSpacing: '2px',
                textTransform: 'uppercase',
                animation: 'countdown-pulse 1s ease-in-out infinite',
              }}
            >
              Drawing winners in...
            </div>

            {/* Number display area */}
            <div
              style={{
                color: 'var(--plexus-color-surface-50)',
                position: 'relative',
                fontSize: '180px',
                fontWeight: 700,
                overflow: 'hidden',
                width: '400px',
                height: '220px',
                margin: '0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span
                key={currentNumber}
                className="countdown-number-in"
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  transformOrigin: 'bottom center',
                  textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                }}
              >
                {currentNumber}
              </span>
            </div>

            {/* Subtitle text */}
            <div
              style={{
                fontSize: '1.125rem',
                fontWeight: 400,
                color: 'var(--plexus-color-positive-200)',
                marginTop: 'var(--plexus-space-lg)',
                letterSpacing: '1px',
              }}
            >
              Get ready for the results!
            </div>
          </div>
        )}

        {/* Final "GO!" message */}
        {showFinal && (
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%) scale(0)',
              textAlign: 'center',
              animation: 'countdown-show 0.5s ease-in forwards',
            }}
          >
            <div
              style={{
                fontSize: '200px',
                fontWeight: 800,
                color: 'var(--plexus-color-surface-50)',
                textShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                letterSpacing: '8px',
              }}
            >
              GO!
            </div>
            <div
              style={{
                fontSize: '1.5rem',
                fontWeight: 500,
                color: 'var(--plexus-color-positive-100)',
                marginTop: 'var(--plexus-space-lg)',
                letterSpacing: '3px',
                textTransform: 'uppercase',
              }}
            >
              Winners selected!
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DrawCountdownAnimation;
